import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { findDOMNode } from 'react-dom'
import { fileSize, getIconName, getIsMail } from 'utils/documents'
import { FormattedMessage, useIntl } from 'react-intl'
import documentActionTypes from 'const/documentActionTypes'
import Icon from 'components/Icon'
import KanBoIcon from 'components/KanBoIcon'
import ElementItem from '../../ElementItem'
import MailPreview from 'components/MailPreview'
import RelativeDate from 'components/RelativeDate'
import classes from './DocumentListItem.module.scss'
import DocumentReferences from 'components/DocumentReferences'
import { useBoardPermission } from 'dataProviders/Board'
import DocumentRename from 'components/DocumentRename'
import withLeftSidebar, { LeftSidebarProps } from 'components/Sidebar/withLeftSidebar'
import sweetalert from 'sweetalert2'
import styled from 'styled-components'
import requestRemoveDocumentFromList from '../../sagas/requestRemoveDocumentFromList'
import requestRemoveDocument from '../../sagas/requestRemoveDocument'
import { optimisticDispatch } from 'utils/sagas'
import {
  setByUrl as setDocumentListItemByUrl,
} from '../../redux/documentListItems'
import { useDispatch, useSelector } from 'react-redux'
import useChannel from 'hooks/useChannel'
import { useCardContext } from 'dataProviders/Card'
import { useDocumentListItemProvider } from 'components/DocumentListItemProvider'
import useMergedRef from '@react-hook/merged-ref'

const TypedRelativeDate: any = RelativeDate

const DraggingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  opacity: 0.8;
  z-index: 2;
`

type Props = LeftSidebarProps & {
  id: number;
  documentListId: number;

  sortable: any;
}

const DocumentListItem = React.forwardRef<ElementItem, Props>(({
  leftSidebar,

  id,
  documentListId,

  sortable
}, ref) => {
  const intl = useIntl()

  const dispatch = useDispatch()

  const permission = useBoardPermission()

  const cardContext = useCardContext()

  const [previewImage, setPreviewImage] = useState(false)
  const [showMailPreview, setShowMailPreview] = useState(false)
  const [showDocumentReferences, setShowDocumentReferences] = useState<{
    documentSourceId: number;
    url: string;
    name: string;
  } | null>(null)

  const documentListItem = useSelector<any, {
    url: string;
    name: string;
    size: number;
    sourceId: number;
    lastModificationTimestamp?: number;
    exists: boolean;
    thumbnailUrl?: string;
    actions: any[];
    isLoaded: boolean;
  }>(state => state['view-card'].documentListItems[id])

  useDocumentListItemProvider(id, documentListItem.sourceId, documentListItem.url)

  useChannel(documentListItem.exists ? `documents-source-${documentListItem.sourceId}` : undefined)

  useEffect(() => {
    if (documentListItem.thumbnailUrl) {
      const image = document.createElement('img')
      image.onload = () => { setPreviewImage(true) }
      image.onerror = () => { setPreviewImage(false) }
      image.src = documentListItem.thumbnailUrl
    } else {
      setPreviewImage(false)
    }
  }, [documentListItem.thumbnailUrl])

  const isCardArchived = useSelector<any, any>(state => state['view-board'].cards[cardContext.id].isArchived)

  const isMail = useMemo(() => getIsMail(documentListItem.name), [documentListItem.name])

  const handleCloseMailPreview = useCallback(() => {
    setShowMailPreview(false)
  }, [])

  const handleClick = useCallback(() => {
    if (isMail) {
      setShowMailPreview(true)
    } else {
      const action = documentListItem.actions.find(action => action.default)

      if (!action) return

      const actionType = documentActionTypes[action.type]

      if (!actionType) return

      actionType.onClick(action)
    }
  }, [isMail, documentListItem.actions])

  const handleDocumentReferencesShow = useCallback(() => {
    setShowDocumentReferences({
      documentSourceId: documentListItem.sourceId,
      url: documentListItem.url,
      name: documentListItem.name,
    })
  }, [documentListItem.sourceId, documentListItem.url, documentListItem.name])

  const handleDocumentReferencesClose = useCallback(() => {
    setShowDocumentReferences(null)
  }, [])

  const handleDocumentRename = useCallback(({ promise, documentSourceId, url, newName }) => {
    const optimisticAction = optimisticDispatch(dispatch, setDocumentListItemByUrl({
      documentSourceId,
      url,
      newUrl: undefined,
      name: newName,
    }))

    promise.finally(() => {
      optimisticAction.revert()
    })
  }, [])

  const handleRemoveClick = useCallback(() => {
    sweetalert({
      text: intl.formatMessage({
        id: 'DOCUMENT_REMOVE_FROM_SOURCE_QUESTION'
      }, {
        name: documentListItem.name,
      }),
      confirmButtonText: intl.formatMessage({ id: 'DELETE' }),
      cancelButtonText: intl.formatMessage({ id: 'CANCEL' }),
      showCancelButton: true,
      type: 'question'
    }).then(() => {
      dispatch(requestRemoveDocument({
        documentSourceId: documentListItem.sourceId,
        url: documentListItem.url,
        documentListItemId: id,
        documentListId,
      }))
    }, () => {})
  }, [documentListItem.sourceId, documentListItem.url, id, documentListItem.name, documentListId])

  const className = (documentListItem.isLoaded && !documentListItem.exists) ? 'list-group-item-danger' : undefined

  const iconName = getIconName(documentListItem.name)

  let compSubtitle: any = null
  if (documentListItem.isLoaded) {
    if (!documentListItem.exists) {
      compSubtitle = (
        <span className='text-danger'>
          <FormattedMessage id='DOCUMENTS_SOURCE_ACCESS_DENIED' />
        </span>
      )
    } else if (typeof documentListItem.size === 'number') {
      compSubtitle = (
        <span>
          <TypedRelativeDate pastOnly timestamp={documentListItem.lastModificationTimestamp} />
          <span className={classes.subtitleDivider}>•</span>
          {fileSize(documentListItem.size)}
        </span>
      )
    }
  }

  let icon: any = null

  if (documentListItem.isLoaded) {
    if (previewImage) {
      // Image preview
      icon = (
        <div className={classes.previewContainer}>
          <img
            className={classes.previewImage}
            src={documentListItem.thumbnailUrl}
            alt=''
          />
        </div>
      )
    } else {
      icon = (
        <Icon
          className={classes.icon}
          fixedWidth
          name={iconName}
        />
      )
    }
  } else {
    icon = <KanBoIcon fixedWidth spin name='kanbo-16-loading' className={classes.icon} />
  }

  const menu = documentListItem.actions.reduce((acc, action) => {
    const actionType = documentActionTypes[action.type]

    if (!actionType) return acc

    acc.push({
      icon: <KanBoIcon fixedWidth name={actionType.iconName} />,
      title: <FormattedMessage id={actionType.formattedMessageId} />,
      onClick: () => actionType.onClick(action, document)
    })

    return acc
  }, [])

  if (documentListItem.isLoaded && documentListItem.exists && permission.member) {
    menu.push({
      icon: <KanBoIcon fixedWidth name='kanbo-16-letter-count' />,
      title: <FormattedMessage id='DOCUMENT_RENAME' />,
      onClick: () => {
        leftSidebar.push(() =>
          <DocumentRename
            documentSourceId={documentListItem.sourceId}
            url={documentListItem.url}
            name={documentListItem.name}
            onRename={handleDocumentRename}
          />
        )
      }
    })
  }

  menu.push({
    icon: <KanBoIcon fixedWidth name='kanbo-16-document-references' />,
    title: <FormattedMessage id='DOCUMENT_REFERENCES' />,
    onClick: handleDocumentReferencesShow
  })

  if (documentListItem.isLoaded && documentListItem.exists && permission.member) {
    menu.push({
      icon: <KanBoIcon fixedWidth name='kanbo-16-delete' />,
      title: <FormattedMessage id='DELETE' />,
      onClick: handleRemoveClick
    })
  }

  if (!isCardArchived && permission.member) {
    menu.push({
      icon: <KanBoIcon fixedWidth name='kanbo-16-detach-from-card' />,
      title: <FormattedMessage id='DETACH' />,
      onClick: () => {
        dispatch(requestRemoveDocumentFromList({
          documentListId,
          documentListItemId: id
        }))
      }
    })
  }

  const mergedRef = useMergedRef(
    ref,
    ref => {
      const node = findDOMNode(ref)
      sortable.connectDragSource(node)
    }
  )

  return (
    <React.Fragment>
      <ElementItem
        ref={mergedRef}
        className={className}
        icon={icon}
        subtitle={compSubtitle}
        menu={menu}
        onClick={handleClick}
      >
        {sortable.isDragging && <DraggingOverlay />}
        {documentListItem.name}
      </ElementItem>
      {showMailPreview &&
        <MailPreview
          name={documentListItem.name}
          url={documentListItem.url}
          sourceId={documentListItem.sourceId}
          onClose={handleCloseMailPreview}
        />
      }
      {showDocumentReferences &&
        <DocumentReferences
          {...showDocumentReferences}
          onClose={handleDocumentReferencesClose}
        />
      }
    </React.Fragment>
  )
})

export default withLeftSidebar(DocumentListItem)
